import React from 'react';
import { Card, CardContent, Badge } from '../ui';
import { Server, Trash2, RefreshCw, Edit2 } from 'lucide-react';

interface ServerCardProps {
  id: string;
  serverName: string;
  serverType: 'stdio' | 'http' | 'sse' | 'websocket' | 'grpc';
  status: 'connected' | 'disconnected' | 'error';
  lastSync?: string;
  lastError?: string;
  clientId?: string;
  onDelete: (id: string) => void;
  onRefresh?: (id: string) => void;
  onEdit?: (id: string) => void;
}

export const ServerCard: React.FC<ServerCardProps> = ({
  id,
  serverName,
  serverType,
  status,
  lastSync,
  lastError,
  onDelete,
  onRefresh,
  onEdit
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'disconnected':
        return 'default';
      case 'error':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getTypeColor = () => {
    switch (serverType) {
      case 'http':
        return 'info';
      case 'sse':
        return 'warning';
      case 'websocket':
        return 'success';
      case 'grpc':
        return 'danger';
      default:
        return 'default';
    }
  };

  return (
    <Card hover>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Server size={20} className="text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">{serverName}</h3>
            </div>
            <div className="flex gap-2">
              <Badge variant={getStatusColor()} size="sm">
                {status || 'unknown'}
              </Badge>
              {serverType && (
                <Badge variant={getTypeColor()} size="sm">
                  {serverType.toUpperCase()}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {onEdit && (
              <button
                onClick={() => onEdit(id)}
                className="text-gray-400 hover:text-blue-600 transition-colors"
                title="Edit server"
              >
                <Edit2 size={18} />
              </button>
            )}
            {onRefresh && status === 'connected' && (
              <button
                onClick={() => onRefresh(id)}
                className="text-gray-400 hover:text-blue-600 transition-colors"
                title="Refresh connection"
              >
                <RefreshCw size={18} />
              </button>
            )}
            <button
              onClick={() => onDelete(id)}
              className="text-gray-400 hover:text-red-600 transition-colors"
              title="Disconnect server"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {lastError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
            <p className="text-xs text-red-800 font-medium">Error:</p>
            <p className="text-xs text-red-600 mt-1">{lastError}</p>
          </div>
        )}

        <div className="text-xs text-gray-500 space-y-1">
          {lastSync && (
            <p>Last synced: {new Date(lastSync).toLocaleString()}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
